/**
 * Purpose: Storage management utilities for quota monitoring, LRU eviction, and cleanup
 *
 * How it works:
 * 1. Monitors storage quota and warns when running low
 * 2. Implements least-recently-USED content eviction (lastAccessedAt, with
 *    message-timestamp fallback for pre-field rows)
 * 3. Cleans up orphaned OPFS files that no longer have IndexedDB references
 * 4. Deletes stale large attachments based on age and size thresholds
 *
 * Key features:
 * - Proactive quota checking before storing large files
 * - Automatic eviction of oldest attachments when space is needed
 * - Stale large attachment cleanup to keep storage usage bounded
 * - Orphan cleanup to prevent storage leaks
 * - Configurable thresholds for different storage pressures
 */

import { db } from "@/indexedDB/db";
import { IS_OPFS_SUPPORTED } from "@/lib/environmentDetector";
import {
  listOPFSAttachments,
  deleteFromOPFS,
  safeFilename,
} from "./opfsStorage";
import {
  deleteCachedMedia,
  mediaCacheKey,
} from "@/middleware/mediaMemoryCache";
import { getAttachmentAccessTime } from "./accessRecency";

/** Storage quota thresholds */
const QUOTA_WARNING_THRESHOLD = 0.8; // 80% - warn user
const QUOTA_CRITICAL_THRESHOLD = 0.9; // 90% - start eviction
const QUOTA_SAFETY_MARGIN = 0.1; // 10% - buffer to keep free
const STALE_ATTACHMENT_MAX_AGE_DAYS = 90;
const STALE_LARGE_ATTACHMENT_THRESHOLD_BYTES = 5 * 1024 * 1024;

/** Storage status levels */
export type StorageStatus = "ok" | "warning" | "critical";

export interface StorageQuotaInfo {
  usage: number;
  quota: number;
  usagePercentage: number;
  status: StorageStatus;
  availableSpace: number;
}

/**
 * Get current storage quota information
 */
export async function getStorageQuota(): Promise<StorageQuotaInfo> {
  if (!navigator.storage?.estimate) {
    return {
      usage: 0,
      quota: 0,
      usagePercentage: 0,
      status: "ok",
      availableSpace: Infinity,
    };
  }

  const estimate = await navigator.storage.estimate();
  const usage = estimate.usage ?? 0;
  const quota = estimate.quota ?? 0;
  const usagePercentage = quota > 0 ? usage / quota : 0;

  let status: StorageStatus = "ok";
  if (usagePercentage >= QUOTA_CRITICAL_THRESHOLD) {
    status = "critical";
  } else if (usagePercentage >= QUOTA_WARNING_THRESHOLD) {
    status = "warning";
  }

  return {
    usage,
    quota,
    usagePercentage,
    status,
    availableSpace: quota - usage,
  };
}

/**
 * Check if there's enough space to store a blob
 *
 * @param blobSize - Size of the blob to store in bytes
 * @returns Object with canStore boolean and quota info
 */
export async function checkQuotaForBlob(blobSize: number): Promise<{
  canStore: boolean;
  quotaInfo: StorageQuotaInfo;
  needsEviction: boolean;
  /** Bytes a sweep must free for canStore to become true (0 when it already is). */
  bytesToFree: number;
}> {
  const quotaInfo = await getStorageQuota();

  // Calculate space needed including safety margin
  const safetyBuffer = quotaInfo.quota * QUOTA_SAFETY_MARGIN;
  const spaceNeeded = blobSize + safetyBuffer;

  const canStore = quotaInfo.availableSpace >= spaceNeeded;
  const needsEviction =
    !canStore ||
    quotaInfo.status === "critical" ||
    quotaInfo.status === "warning";

  // How much a sweep must actually free for canStore to become true. Asking
  // eviction for only blob.size (while the gate demands blob.size + the
  // safety buffer) meant a ≥90%-full device could NEVER satisfy the gate —
  // every store re-entered the sweep and the eviction front marched through
  // the entire table over a session.
  const bytesToFree = canStore
    ? 0
    : Math.max(blobSize, spaceNeeded - quotaInfo.availableSpace);

  return { canStore, quotaInfo, needsEviction, bytesToFree };
}

/**
 * Get attachments sorted least-recently-USED first, for eviction.
 * Recency = `lastAccessedAt` (touched on every content read/write), falling
 * back to the MESSAGE timestamp for rows that predate the field. Sorting by
 * message time alone was anti-recency: the viewer's swipe-back direction goes
 * toward OLDER messages, so "the video the user will return to" and "the
 * sweep's first victim" were the same row by construction.
 */
async function getAttachmentsByAge(): Promise<
  Array<{ id: string; recency: number; type: "media" | "file" }>
> {
  const [mediaAttachments, fileAttachments] = await Promise.all([
    db.mediaAttachments.toArray(),
    db.fileAttachments.toArray(),
  ]);

  // Epoch ms either way. An unparseable/absent timestamp ranks LAST (not
  // first): rows with unknown age must not become permanent first victims —
  // a streamed video was once persisted with timestamp "" and sat at the
  // front of every sweep.
  const recencyOf = (a: {
    id: string;
    lastAccessedAt?: number;
    timestamp: string;
  }): number => {
    // Session recency first (memory-only — reads never write, see
    // accessRecency.ts), then the write-time stamp, then message time.
    const sessionAccess = getAttachmentAccessTime(a.id);
    if (sessionAccess) {
      return sessionAccess;
    }
    if (a.lastAccessedAt) {
      return a.lastAccessedAt;
    }
    const parsed = Date.parse(a.timestamp);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  };

  const allAttachments = [
    ...mediaAttachments.map((a) => ({
      id: a.id,
      recency: recencyOf(a),
      type: "media" as const,
    })),
    ...fileAttachments.map((a) => ({
      id: a.id,
      recency: recencyOf(a),
      type: "file" as const,
    })),
  ];

  return allAttachments.sort((a, b) => a.recency - b.recency);
}

/**
 * Evict oldest attachments to free up space
 *
 * @param bytesNeeded - Amount of space to free in bytes
 * @returns Number of attachments evicted
 */
export async function evictOldestAttachments(
  bytesNeeded: number,
  /** The attachment currently being stored — never evict what we're writing. */
  excludeId?: string,
): Promise<number> {
  // Anything touched after this instant landed mid-sweep (e.g. a concurrent
  // download finishing while we iterate) — the recency snapshot below never
  // saw it, so it must be spared explicitly (excludeId only covers the ONE
  // store that triggered this sweep, not its siblings).
  const sweepStart = Date.now();
  const attachments = await getAttachmentsByAge();
  let freedSpace = 0;
  let evictedCount = 0;

  let opfsSizeMap = new Map<string, number>();
  if (IS_OPFS_SUPPORTED) {
    try {
      const opfsFiles = await listOPFSAttachments();
      opfsSizeMap = new Map(opfsFiles.map((file) => [file.id, file.size]));
    } catch {
      // ignore OPFS listing errors and fall back to metadata/IndexedDB sizes
    }
  }

  // Eviction frees CONTENT ONLY — the row and its thumbnail always survive.
  // The original sweep deleted whole rows, which (a) blanked every bubble's
  // thumbnail across all chats at once, and (b) killed the file-backed blobs
  // behind images currently on screen (an IDB-read Blob is a handle into the
  // row's blob storage — deleting the row breaks it retroactively). Content is
  // safe to drop: readers see null and re-download from the bucket on demand.
  for (const attachment of attachments) {
    if (freedSpace >= bytesNeeded) {
      break;
    }
    if (attachment.id === excludeId) {
      continue;
    }
    // Touched after the sweep began → a concurrent store/serve just landed
    // this content; the ordering snapshot ranked it by its OLD recency (or
    // never saw it). Evicting it would destroy brand-new bytes.
    const sessionAccess = getAttachmentAccessTime(attachment.id);
    if (sessionAccess !== undefined && sessionAccess > sweepStart) {
      continue;
    }

    try {
      if (attachment.type === "media") {
        const entity = await db.mediaAttachments.get(attachment.id);
        if (entity && (entity.content || entity.contentStoredIn === "opfs")) {
          let contentSize = 0;
          if (entity.contentStoredIn === "opfs") {
            // The OPFS listing reports slash-ENCODED filenames; a raw-id
            // lookup scored scoped ids as 0 bytes, so freedSpace never grew
            // and the loop swept the entire table.
            const listedSize = opfsSizeMap.get(safeFilename(attachment.id));
            // No listing entry means the listing FAILED (listOPFSAttachments
            // returns [] on any OPFS fault — evicting would destroy good
            // content device-wide for zero credit), or the file was written
            // after the listing snapshot (evicting would destroy a concurrent
            // download), or the pointer is already dangling (evicting frees
            // nothing). Skipping is correct in all three cases — dangling
            // pointers self-heal at read time via the null/liveness
            // re-download path.
            if (listedSize === undefined) {
              continue;
            }
            contentSize = listedSize;

            const deleted = await deleteFromOPFS(attachment.id);
            // A cleared pointer over surviving bytes strands them forever —
            // the orphan sweep can't reclaim files whose encoded name matches
            // a live row id.
            if (!deleted) {
              continue;
            }
          } else {
            contentSize = entity.content?.size ?? 0;
          }

          await db.mediaAttachments.update(attachment.id, {
            content: undefined,
            contentStoredIn: undefined,
          });
          // The in-memory cache may hold the very handle whose backing was
          // just deleted — serving it would render broken images.
          deleteCachedMedia(mediaCacheKey(attachment.id, "content"));

          freedSpace += contentSize;
          evictedCount++;
        }
      } else {
        const entity = await db.fileAttachments.get(attachment.id);
        if (entity && (entity.content || entity.contentStoredIn === "opfs")) {
          let contentSize = entity.content?.size ?? 0;
          if (entity.contentStoredIn === "opfs") {
            // Same skip-if-absent rule as the media branch. This branch used
            // to credit entity.fileSize for unlisted rows on the grounds that
            // documents were capped at 20 MB so a mis-credit was bounded;
            // with MAX_DOC_FILE_SIZE at 100 MB one phantom credit would make
            // the sweep believe it freed 5x what it did. An absent listing
            // entry means the listing failed, the file landed after the
            // snapshot, or the pointer is already dangling — skipping is
            // correct in all three (see the media branch above).
            const listedSize = opfsSizeMap.get(safeFilename(attachment.id));
            if (listedSize === undefined) {
              continue;
            }
            contentSize = listedSize;
          }

          if (entity.contentStoredIn === "opfs" && IS_OPFS_SUPPORTED) {
            const deleted = await deleteFromOPFS(attachment.id);
            // Same stranding hazard as the media branch: never clear a
            // pointer over bytes that survived the delete.
            if (!deleted) {
              continue;
            }
          }
          await db.fileAttachments.update(attachment.id, {
            content: undefined,
            contentStoredIn: undefined,
          });

          freedSpace += contentSize;
          evictedCount++;
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error(
          `[Storage] Failed to evict attachment ${attachment.id}:`,
          error,
        );
      }
    }
  }

  if (process.env.NODE_ENV === "development" && evictedCount > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `[Storage] Evicted ${evictedCount} attachments, freed ${(freedSpace / 1024 / 1024).toFixed(2)} MB`,
    );
  }

  return evictedCount;
}

/**
 * Clean up orphaned OPFS files that don't have corresponding IndexedDB entries
 * This can happen if the app crashes during deletion or if there's a sync issue
 *
 * @returns Number of orphaned files cleaned up
 */
export async function cleanOrphanedOPFSFiles(): Promise<number> {
  if (!IS_OPFS_SUPPORTED) {
    return 0;
  }

  try {
    // Get all OPFS file IDs
    const opfsFiles = await listOPFSAttachments();
    if (opfsFiles.length === 0) {
      return 0;
    }

    // Get all attachment IDs from IndexedDB
    const [mediaIds, fileIds] = await Promise.all([
      db.mediaAttachments.toCollection().primaryKeys(),
      db.fileAttachments.toCollection().primaryKeys(),
    ]);

    // OPFS filenames are slash-ENCODED; encode the raw IDB ids the same way or
    // every scoped id ("chatId/uuid") reads as an orphan and its content is
    // deleted on every boot while its row still points at OPFS.
    const validIds = new Set(
      [...mediaIds, ...fileIds].map((id) => safeFilename(String(id))),
    );

    // Find orphaned files — skipping anything written in the last 10 minutes:
    // content is written to OPFS BEFORE its IndexedDB row exists, so a young
    // row-less file is usually a write in flight, not garbage.
    const ORPHAN_MIN_AGE_MS = 10 * 60 * 1000;
    const now = Date.now();
    const orphanedFiles = opfsFiles.filter(
      (f) =>
        !validIds.has(f.id) && now - (f.lastModified ?? 0) > ORPHAN_MIN_AGE_MS,
    );

    // Delete orphaned files
    let cleanedCount = 0;
    for (const file of orphanedFiles) {
      const deleted = await deleteFromOPFS(file.id);
      if (deleted) {
        cleanedCount++;
      }
    }

    if (process.env.NODE_ENV === "development" && cleanedCount > 0) {
      // eslint-disable-next-line no-console
      console.log(`[Storage] Cleaned up ${cleanedCount} orphaned OPFS files`);
    }

    return cleanedCount;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[Storage] Failed to clean orphaned files:", error);
    }
    return 0;
  }
}

async function deleteStaleAttachments(
  maxAgeDays: number,
  minSizeBytes: number,
): Promise<number> {
  const now = Date.now();
  const cutoffTime = now - maxAgeDays * 24 * 60 * 60 * 1000;

  let deletedCount = 0;

  const [mediaAttachments, fileAttachments] = await Promise.all([
    db.mediaAttachments.toArray(),
    db.fileAttachments.toArray(),
  ]);

  for (const entity of mediaAttachments) {
    let timestamp = Number.NaN;
    try {
      timestamp = new Date(entity.timestamp).getTime();
    } catch {
      // ignore parse errors
    }

    if (!Number.isFinite(timestamp) || timestamp >= cutoffTime) {
      continue;
    }

    const contentSize = entity.content?.size ?? 0;
    // OPFS-stored media may have no IndexedDB blob; treat them as large based on age only
    if (contentSize < minSizeBytes && entity.contentStoredIn !== "opfs") {
      continue;
    }

    try {
      if (entity.contentStoredIn === "opfs" && IS_OPFS_SUPPORTED) {
        const deleted = await deleteFromOPFS(entity.id);
        // Clearing the pointer over bytes that survived the delete strands
        // them forever (see evictOldestAttachments) — keep the row intact.
        if (!deleted) {
          continue;
        }
      }

      // Content only — the row and its thumbnail survive (see
      // evictOldestAttachments for why whole-row deletes are destructive).
      await db.mediaAttachments.update(entity.id, {
        content: undefined,
        contentStoredIn: undefined,
      });
      deleteCachedMedia(mediaCacheKey(entity.id, "content"));
      deletedCount++;
    } catch {
      // ignore deletion errors for individual entities
    }
  }

  for (const entity of fileAttachments) {
    let timestamp = Number.NaN;
    try {
      timestamp = new Date(entity.timestamp).getTime();
    } catch {
      // ignore parse errors
    }

    if (!Number.isFinite(timestamp) || timestamp >= cutoffTime) {
      continue;
    }

    const sizeFromMeta = entity.fileSize ?? entity.content?.size ?? 0;
    if (sizeFromMeta < minSizeBytes) {
      continue;
    }

    try {
      if (entity.contentStoredIn === "opfs" && IS_OPFS_SUPPORTED) {
        const deleted = await deleteFromOPFS(entity.id);
        // Same stranding hazard as the media loop above.
        if (!deleted) {
          continue;
        }
      }

      await db.fileAttachments.update(entity.id, {
        content: undefined,
        contentStoredIn: undefined,
      });
      deletedCount++;
    } catch {
      // ignore deletion errors for individual entities
    }
  }

  return deletedCount;
}

/**
 * Run storage maintenance tasks
 * - Clean orphaned files
 * - Delete stale large attachments based on age and size thresholds
 * - Check quota and evict if critical
 *
 * Call this on app initialization
 */
export async function runStorageMaintenance(): Promise<void> {
  try {
    // Clean orphaned files first
    await cleanOrphanedOPFSFiles();

    await deleteStaleAttachments(
      STALE_ATTACHMENT_MAX_AGE_DAYS,
      STALE_LARGE_ATTACHMENT_THRESHOLD_BYTES,
    );

    // Check quota and evict if needed
    const quotaInfo = await getStorageQuota();
    if (quotaInfo.status === "critical") {
      // Evict enough to get below warning threshold
      const targetUsage = quotaInfo.quota * QUOTA_WARNING_THRESHOLD;
      const bytesToFree = quotaInfo.usage - targetUsage;
      if (bytesToFree > 0) {
        await evictOldestAttachments(bytesToFree);
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[Storage] Maintenance failed:", error);
    }
  }
}
